import { Paperclip } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import type { MessageAttachment } from '@/schemas/commons';

import type { AttachmentBubbleProps } from './AttachmentBubble.types';

// Stable key without holding the full URL (legacy `data:` URIs run ~1 MB):
// the URL tail is unique per attachment (base64 tail / S3 object key).
const attachmentKey = (att: MessageAttachment) => att.url.slice(-64);

const isImage = (att: MessageAttachment) => att.contentType.startsWith('image/');

/** Attachments rendered inside the sender's bubble, WhatsApp style. */
export const AttachmentBubble = ({ attachments }: AttachmentBubbleProps) => {
	const { t } = useTranslation('translation', { keyPrefix: 'conversationDetail' });
	const images = attachments.filter(isImage);
	const files = attachments.filter((att) => !isImage(att));

	return (
		<div className="flex flex-col gap-2xs">
			{images.length > 0 && (
				<div className={`grid gap-2xs ${images.length > 1 ? 'grid-cols-2' : 'grid-cols-1'}`}>
					{images.map((att) => (
						<a key={attachmentKey(att)} href={att.url} target="_blank" rel="noreferrer" className="block">
							<img
								src={att.url}
								alt={att.name || t('ATTACHMENT_IMAGE_ALT')}
								loading="lazy"
								className="max-h-[260px] w-full rounded-md object-cover"
							/>
						</a>
					))}
				</div>
			)}
			{files.map((att) => (
				<a
					key={attachmentKey(att)}
					href={att.url}
					target="_blank"
					rel="noreferrer"
					className="flex items-center gap-2xs rounded-md bg-white/70 px-xs py-2xs text-[12px] text-default transition-colors hover:bg-white"
				>
					<Paperclip className="h-[13px] w-[13px] shrink-0 text-subtle" />
					<span className="min-w-0 truncate">{att.name || t('ATTACHMENT_PDF_OPEN')}</span>
				</a>
			))}
		</div>
	);
};
